import axios from "axios";
import { refreshToken } from "./auth";
import { retryDelayMs, shouldRetry503 } from "./retry503";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

let isRefreshing = false;
// Cada item é { resolve, reject } — precisa dos dois porque agora uma
// falha de rede ao renovar (ver services/auth.js) deve REJEITAR as
// requisições enfileiradas, não tentar retry com um token nulo/velho.
let queue = [];

api.interceptors.request.use((config) => {
  // Aluno com plano vencido assinando o Plus pela tela de login (componente
  // ReactivationModal): o token de reativação só abre as rotas /payments, então
  // só vai nelas. Fora disso, comportamento de sempre.
  let reactivationToken = null;
  if (config.url?.startsWith("/payments")) {
    try {
      reactivationToken = sessionStorage.getItem("reactivationToken");
    } catch (e) {
      // sessionStorage indisponível (modo privado etc): sem token de reativação.
    }
  }
  const token = reactivationToken || localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  res => res,
  async (error) => {
    const originalRequest = error.config;

    // 503 "servidor ocupado" (limite de requisições simultâneas do backend):
    // repete sozinho o que é seguro repetir, respeitando o Retry-After. Se
    // persistir, o erro segue pra tela com a mensagem do backend. Ver
    // services/retry503.js (R270).
    if (shouldRetry503(error)) {
      originalRequest._retry503Count = (originalRequest._retry503Count || 0) + 1;
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs(error)));
      return api(originalRequest);
    }

    const isLoginRequest =
      originalRequest?.url &&
      (originalRequest.url.includes("/login") ||
        originalRequest.url.includes("/auth/login"));

    // Rate limiting (429 Too Many Requests)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || '600';
      const event = new CustomEvent('loginRateLimited', {
        detail: { retryAfter: parseInt(retryAfter) }
      });
      window.dispatchEvent(event);
      return Promise.reject(error);
    }

    // Conta desativada (is_active=False, ou plano vencido além da carência
    // -- ver get_current_user em app/utils/auth.py) pode vir de QUALQUER
    // endpoint autenticado agora, não só do /login -- antes disso só era
    // checado lá. Sem isto, o usuário ficava preso no app vendo erros
    // genéricos ("Erro no check-in" etc) sem entender que a conta foi
    // desativada. Desloga e redireciona pro /login igual ao caso de refresh
    // token rejeitado (abaixo), mas passa a mensagem adiante via
    // sessionStorage pra reaproveitar a UI que Login.js já tem pronta pra
    // detail.code === "account_disabled" (mesmo formato de
    // build_account_disabled_detail em user_service.py). isLoginRequest fica
    // de fora: nesse caso o próprio Login.js já mostra o erro no formulário.
    const detail403 = error.response?.data?.detail;
    if (
      error.response?.status === 403 &&
      !isLoginRequest &&
      detail403 &&
      typeof detail403 === "object" &&
      detail403.code === "account_disabled"
    ) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("expiresAt");
      localStorage.removeItem("refreshToken");
      try {
        sessionStorage.setItem("accountDisabledDetail", JSON.stringify(detail403));
      } catch (e) {
        // sessionStorage indisponível (modo privado etc) -- segue sem a
        // mensagem pré-carregada, Login.js só não mostra o aviso de cara.
      }
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isLoginRequest
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      let newToken = null;
      let networkError = null;
      try {
        newToken = await refreshToken();
      } catch (err) {
        // Falha de rede/servidor ao renovar (ver services/auth.js) — NÃO é
        // motivo pra deslogar. Propaga o erro original da requisição, sem
        // mexer no localStorage, pra sessão continuar válida na próxima
        // tentativa (próxima requisição, ou o timer em useSessionTimer.js).
        networkError = err;
      }

      isRefreshing = false;

      if (networkError) {
        queue.forEach(({ reject }) => reject(error));
        queue = [];
        return Promise.reject(error);
      }

      if (!newToken) {
        // O backend respondeu e rejeitou o refresh token (expirado/revogado
        // de verdade) — aqui sim é logout genuíno.
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("expiresAt");
        localStorage.removeItem("refreshToken");
        queue.forEach(({ reject }) => reject(error));
        queue = [];
        window.location.href = "/login";
        return Promise.reject(error);
      }

      queue.forEach(({ resolve }) => resolve(newToken));
      queue = [];

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default api;
